import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Card, Skeleton, Tabs } from "@/design-system/components/ui";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import { IncomeOutflowPanel } from "@/components/portfolioAdmin/loanDetail/financials/IncomeOutflowPanel";
import { IncomePoolsPanel } from "@/components/portfolioAdmin/loanDetail/financials/IncomePoolsPanel";
import { PoolPeriodDrawer } from "@/components/portfolioAdmin/loanDetail/financials/PoolPeriodDrawer";
import {
  BrokerScheduleTable,
  BuyerScheduleTable,
  SellerScheduleTable,
  TpsScheduleTable,
} from "@/components/portfolioAdmin/loanDetail/financials/scheduleTables";
import { AllocationDrawer } from "@/components/portfolioAdmin/loanDetail/paymentTracking/AllocationDrawer";
import { useLoanAmortization } from "@/hooks/portfolioAdmin/useAmortization";

interface FinancialsTabProps {
  loan: LoanSummaryDto;
}

/**
 * Loan Financials tab — Financials redesign. Five view-only tables behind a
 * single named switcher (the design-system Tabs — deliberately NOT a
 * chevron/arrow pager, which is the Fee & Obligation tab's period-navigation
 * idiom; this control picks one of N named tables, it never steps a
 * sequence): the Buyer / Seller / Broker / TPS per-period schedules (one
 * amortization read — the servicing ledger's period spine) and the Phoenix
 * Income Pools booked ledger (a projection over recognized IncomeEvents,
 * owner ruling D-18; the legacy Income & Outflow roll-up lives beneath it as
 * the supporting whole-loan summary). Every table paginates at 50 rows and
 * every row drills into a factual detail drawer, deep-linkable via
 * `?table=<id>&row=<key>` (row = the period's paymentId for schedules, the
 * month key for pool periods). Strictly read-only — corrections happen
 * upstream before booking, never by editing booked history.
 */
const TABLE_TABS = [
  { id: "buyer", label: "Buyer" },
  { id: "seller", label: "Seller" },
  { id: "broker", label: "Broker" },
  { id: "tps", label: "TPS" },
  { id: "income-pools", label: "Phoenix Income Pools" },
] as const;

type TableId = (typeof TABLE_TABS)[number]["id"];

const TABLE_IDS = TABLE_TABS.map((t) => t.id) as readonly string[];

export function FinancialsTab({ loan }: FinancialsTabProps) {
  const [params, setParams] = useSearchParams();

  const rawTable = params.get("table");
  const activeTable: TableId =
    rawTable && TABLE_IDS.includes(rawTable) ? (rawTable as TableId) : "buyer";
  const row = params.get("row");

  const setTable = (id: string) => {
    const updated = new URLSearchParams(params);
    updated.set("table", id);
    updated.delete("row"); // a row key only means something within its table
    setParams(updated, { replace: true });
  };
  const setRow = (key: string | null) => {
    const updated = new URLSearchParams(params);
    if (key) updated.set("row", key);
    else updated.delete("row");
    setParams(updated, { replace: true });
  };

  const amortization = useLoanAmortization(loan.id);

  const renderSchedule = (render: (data: NonNullable<typeof amortization.data>) => ReactNode) => {
    if (amortization.isLoading) {
      return (
        <Card data-testid="financials-schedule-loading">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-5 w-full mt-space-3" />
          <Skeleton className="h-5 w-full mt-space-2" />
        </Card>
      );
    }
    if (amortization.isError || !amortization.data) {
      return (
        <Card data-testid="financials-schedule-error">
          <p className="text-small text-status-error m-0 mb-space-2">
            The schedule could not be loaded.
          </p>
          <Button variant="secondary" size="sm" onClick={() => void amortization.refetch()}>
            Retry
          </Button>
        </Card>
      );
    }
    return <Card>{render(amortization.data)}</Card>;
  };

  return (
    <div className="flex flex-col gap-space-4" data-testid="financials-tab">
      <Tabs
        tabs={[...TABLE_TABS]}
        activeId={activeTable}
        onChange={setTable}
        ariaLabel="Financials tables"
      >
        {activeTable === "buyer" &&
          renderSchedule((d) => (
            <BuyerScheduleTable periods={d.buyerSchedule} onOpenAllocation={setRow} />
          ))}
        {activeTable === "seller" &&
          renderSchedule((d) => (
            <SellerScheduleTable periods={d.sellerSchedule} onOpenAllocation={setRow} />
          ))}
        {activeTable === "broker" &&
          renderSchedule((d) => (
            <BrokerScheduleTable periods={d.brokerSchedule} onOpenAllocation={setRow} />
          ))}
        {activeTable === "tps" &&
          renderSchedule((d) => (
            <TpsScheduleTable periods={d.tpsSchedule} onOpenAllocation={setRow} />
          ))}
        {activeTable === "income-pools" && (
          <div className="flex flex-col gap-space-4">
            <IncomePoolsPanel loanId={loan.id} onOpenPeriod={setRow} />
            <IncomeOutflowPanel loanId={loan.id} />
          </div>
        )}
      </Tabs>

      {activeTable === "income-pools" ? (
        <PoolPeriodDrawer loanId={loan.id} periodStart={row} onClose={() => setRow(null)} />
      ) : (
        <AllocationDrawer paymentId={row} onClose={() => setRow(null)} />
      )}
    </div>
  );
}
